"use client";

import { useState, useTransition } from "react";
import { ShieldCheck, FileText, Search, Upload, Info, MessageSquare, X, Loader2 } from "lucide-react";
import { submitCancellationAction, trackCancellationAction } from "@/actions/tenant/tenant-cancellations";
import { sendOtpAction, verifyOtpAction } from '@/actions/sms-actions';

const branches = ["Kasko Poliçesi", "Trafik Poliçesi", "İMM Poliçesi", "Kısa Süreli Trafik Poliçesi"];

export default function PoliceIptal() {
    const [isPending, startTransition] = useTransition();
    const [activeTab, setActiveTab] = useState<"talep" | "sorgu">("talep");
    const [loading, setLoading] = useState(false);

    // SMS Doğrulama (OTP)
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [otpActionType, setOtpActionType] = useState<"talep" | "sorgu" | null>(null);

    // Formlar
    const [requestForm, setRequestForm] = useState({ branch: "", identity: "", plate: "", phone: "", file: null as File | null, note: "" });
    const [queryForm, setQueryForm] = useState({ branch: "", identity: "", phone: "" });
    const [queryResult, setQueryResult] = useState<any>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            // Sadece PDF kontrolü
            if (e.target.files[0].type !== "application/pdf") {
                alert("Lütfen sadece PDF formatında dosya yükleyiniz.");
                return;
            }
            setRequestForm({ ...requestForm, file: e.target.files[0] });
        }
    };

    // 1. ADIM: SMS GÖNDERME
    const initiateAction = async (e: React.FormEvent, type: "talep" | "sorgu") => {
        e.preventDefault();

        const phone = type === "talep" ? requestForm.phone : queryForm.phone;
        if (!phone) return alert("Lütfen telefon numaranızı girin.");
        if (type === "talep" && !requestForm.file) return alert("Lütfen Noter Satış Belgesi (PDF) yükleyiniz.");

        setLoading(true);
        try {
            const result = await sendOtpAction(phone);

            if (result.success) {
                setOtpActionType(type);
                setShowOtpModal(true);
            } else {
                alert(result.message || "SMS gönderilirken bir hata oluştu.");
            }
        } catch (error) {
            alert("Teknik bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const handleOtpVerify = async () => {
        if (otpCode.length < 6) return alert("Lütfen 6 haneli doğrulama kodunu girin.");

        const phone = otpActionType === "talep" ? requestForm.phone : queryForm.phone;

        setLoading(true);
        try {
            const result = await verifyOtpAction(phone, otpCode);

            if (result.success) {
                setShowOtpModal(false);
                setOtpCode("");

                if (otpActionType === "talep") {
                    await executeRequestSubmit();
                } else {
                    await executeQuerySubmit();
                }
            } else {
                alert(result.message || "Doğrulama kodu hatalı veya süresi dolmuş.");
            }
        } catch (error: any) {
            alert("Doğrulama sırasında teknik bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const executeRequestSubmit = async () => {
        const uploadData = new FormData();
        uploadData.append('file', requestForm.file!);

        const uploadRes = await fetch('/api/cancellations/upload', {
            method: 'POST',
            body: uploadData
        });
        const uploadResult = await uploadRes.json();

        if (!uploadResult.success) {
            alert(uploadResult.error || "Dosya yüklenirken hata oluştu.");
            return;
        }

        // GÜVENLİK YAMASI: API 'fileName' veya eski adıyla 'url' dönüyor olabilir. İkisini de yakalayalım.
        const finalPdfUrl = uploadResult.fileName || uploadResult.url;

        if (!finalPdfUrl) {
            alert("Sistem Hatası: Dosya yüklendi ancak dosya yolu alınamadı.");
            return;
        }

        const res = await submitCancellationAction({
            branch: requestForm.branch,
            identity: requestForm.identity,
            plate: requestForm.plate,
            phone: requestForm.phone,
            note: requestForm.note,
            pdfUrl: finalPdfUrl // Artık buranın kesinlikle dolu olduğundan eminiz!
        });

        if (res.success) {
            alert("İptal talebiniz başarıyla alınmıştır. Müşteri temsilcilerimiz en kısa sürede işleme alacaktır.");
            setRequestForm({ branch: "", identity: "", plate: "", phone: "", file: null, note: "" });
        } else {
            alert(res.error);
        }
    };

    const executeQuerySubmit = async () => {
        const res = await trackCancellationAction(queryForm);
        if (res.success) {
            setQueryResult(res.data);
        } else {
            alert(res.error);
            setQueryResult(null);
        }
    };

    return (
        <section className="bg-slate-50 min-h-screen py-20 relative">
            <div className="container mx-auto px-4 max-w-4xl">
                {/* Başlık Alanı */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center p-4 rounded-full mb-4 shadow-sm" style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary), transparent 90%)' }}>
                        <ShieldCheck className="w-10 h-10" style={{ color: 'var(--color-primary)' }} />
                    </div>
                    <h1 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">
                        Poliçe İptal <span style={{ color: 'var(--color-primary)' }}>İşlemleri</span>
                    </h1>
                    <p className="text-slate-600 text-lg font-medium">Mevcut poliçenizi iptal etmek veya iptal talebinizin durumunu sorgulamak için aşağıdaki alanları kullanabilirsiniz.</p>
                </div>

                {/* Tab Butonları */}
                <div className="flex bg-white rounded-2xl shadow-sm border border-slate-200 p-2 mb-8">
                    <button
                        onClick={() => { setActiveTab("talep"); setQueryResult(null); }}
                        className={`flex-1 py-4 text-center rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${activeTab === "talep" ? "shadow-md text-white" : "text-slate-500 hover:bg-slate-50"}`}
                        style={{ backgroundColor: activeTab === "talep" ? 'var(--color-primary)' : 'transparent' }}
                    >
                        <FileText size={20} /> İptal Talebi Oluştur
                    </button>
                    <button
                        onClick={() => setActiveTab("sorgu")}
                        className={`flex-1 py-4 text-center rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${activeTab === "sorgu" ? "shadow-md text-white" : "text-slate-500 hover:bg-slate-50"}`}
                        style={{ backgroundColor: activeTab === "sorgu" ? 'var(--color-primary)' : 'transparent' }}
                    >
                        <Search size={20} /> Durumu Sorgula
                    </button>
                </div>

                {/* Tab İçerikleri */}
                <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 md:p-12">
                    {/* 1. TALEP FORMU */}
                    {activeTab === "talep" && (
                        <form onSubmit={(e) => initiateAction(e, "talep")} className="space-y-6 animate-in fade-in">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Branş Seçiniz</label>
                                    <select required className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-slate-50 text-slate-700 font-medium"
                                        style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                                        value={requestForm.branch} onChange={(e) => setRequestForm({ ...requestForm, branch: e.target.value })}>
                                        <option value="" disabled>Seçiniz</option>
                                        {branches.map(b => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">TCKN / VKN</label>
                                    <input required type="text" maxLength={11} className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 bg-slate-50 text-slate-700 font-medium"
                                        style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                                        value={requestForm.identity} onChange={(e) => setRequestForm({ ...requestForm, identity: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Plaka</label>
                                    <input required type="text" placeholder="Örn: 34ABC123" className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 bg-slate-50 uppercase text-slate-700 font-medium"
                                        style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                                        value={requestForm.plate} onChange={(e) => setRequestForm({ ...requestForm, plate: e.target.value.toUpperCase() })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Telefon Numarası</label>
                                    <input required type="tel" placeholder="05XX XXX XX XX" className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 bg-slate-50 text-slate-700 font-medium"
                                        style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                                        value={requestForm.phone} onChange={(e) => setRequestForm({ ...requestForm, phone: e.target.value })} />
                                </div>
                            </div>

                            <div className="mt-8">
                                <label className="block text-sm font-bold text-slate-700 mb-2">Noter Satış Belgesi (Sadece PDF)</label>
                                <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center hover:bg-slate-50 transition-colors cursor-pointer relative"
                                    style={{ borderColor: requestForm.file ? 'var(--color-primary)' : '' }}>
                                    <input type="file" accept=".pdf" onChange={handleFileChange} required className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                    <Upload className="w-10 h-10 mx-auto mb-4 text-slate-400" style={{ color: requestForm.file ? 'var(--color-primary)' : '' }} />
                                    <p className="text-slate-700 font-bold">
                                        {requestForm.file ? requestForm.file.name : "Dosya seçmek veya sürüklemek için tıklayın"}
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6">
                                <label className="block text-sm font-bold text-slate-700 mb-2">Ek Notlar (İsteğe Bağlı)</label>
                                <textarea rows={3} placeholder="Belirtmek istediğiniz detaylar..."
                                    className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 bg-slate-50 text-slate-700 resize-none transition-all font-medium"
                                    style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                                    value={requestForm.note} onChange={(e) => setRequestForm({ ...requestForm, note: e.target.value })}
                                />
                            </div>

                            <button type="submit" disabled={loading} className="w-full py-4 rounded-xl text-white font-black text-lg transition-all hover:scale-[1.02] shadow-lg flex justify-center items-center gap-2 mt-8 disabled:opacity-70"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {loading ? <Loader2 className="animate-spin" /> : "Talebi Gönder"}
                            </button>
                        </form>
                    )}

                    {/* 2. SORGU FORMU */}
                    {activeTab === "sorgu" && (
                        <div className="animate-in fade-in">
                            <form onSubmit={(e) => initiateAction(e, "sorgu")} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Branş</label>
                                    <select required className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 bg-slate-50 text-slate-700 font-medium"
                                        style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                                        value={queryForm.branch} onChange={(e) => setQueryForm({ ...queryForm, branch: e.target.value })}>
                                        <option value="" disabled>Seçiniz</option>
                                        {branches.map(b => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">TCKN / VKN</label>
                                    <input required type="text" maxLength={11} className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 bg-slate-50 text-slate-700 font-medium"
                                        style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                                        value={queryForm.identity} onChange={(e) => setQueryForm({ ...queryForm, identity: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Telefon</label>
                                    <input required type="tel" className="w-full px-4 py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 bg-slate-50 text-slate-700 font-medium"
                                        style={{ '--tw-ring-color': 'var(--color-primary)' } as any}
                                        value={queryForm.phone} onChange={(e) => setQueryForm({ ...queryForm, phone: e.target.value })} />
                                </div>
                                <div className="md:col-span-3 mt-4">
                                    <button type="submit" disabled={loading} className="w-full py-4 rounded-xl text-white font-black transition-all shadow-md flex justify-center items-center gap-2"
                                        style={{ backgroundColor: 'var(--color-primary)' }}>
                                        {loading ? <Loader2 className="animate-spin" /> : <><Search size={20} /> Durumu Sorgula</>}
                                    </button>
                                </div>
                            </form>

                            {/* Sorgu Sonucu */}
                            {queryResult && (
                                <div className="mt-10 p-6 rounded-2xl border flex items-start gap-4 animate-in slide-in-from-bottom-4"
                                    style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary), transparent 95%)', borderColor: 'color-mix(in srgb, var(--color-primary), transparent 80%)' }}>
                                    <Info className="w-8 h-8 shrink-0" style={{ color: 'var(--color-primary)' }} />
                                    <div>
                                        <h3 className="font-black text-lg text-slate-900 mb-1">Talep Durumu: <span style={{ color: 'var(--color-primary)' }}>{queryResult.status}</span></h3>
                                        <p className="text-slate-600 mb-2 font-medium">
                                            <strong>{queryResult.plate}</strong> plakalı <strong>{queryResult.branch}</strong> iptal talebiniz sistemimizde kayıtlıdır.
                                        </p>
                                        <p className="text-sm font-bold text-slate-500">
                                            Talep Tarihi: {new Date(queryResult.createdAt).toLocaleDateString('tr-TR')}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* --- SMS MODALI --- */}
            {showOtpModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-100 p-8 text-center relative animate-in zoom-in-95">
                        <button onClick={() => setShowOtpModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X size={24} /></button>
                        <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'color-mix(in srgb, var(--color-primary), transparent 90%)' }}>
                            <MessageSquare className="w-8 h-8" style={{ color: 'var(--color-primary)' }} />
                        </div>
                        <h3 className="text-2xl font-black text-slate-900 mb-2">SMS Doğrulama</h3>
                        <p className="text-slate-500 mb-6 text-sm font-medium">
                            <strong className="text-slate-800">
                                {otpActionType === "talep" ? requestForm.phone : queryForm.phone}
                            </strong> numaralı telefonunuza gönderilen <span className="text-blue-600 font-bold">6 haneli</span> kodu giriniz.
                        </p>
                        <input type="text" maxLength={6} placeholder="000000" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                            className="w-full text-center text-3xl tracking-[0.5em] font-black px-4 py-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 mb-6 bg-slate-50 text-slate-700"
                            style={{ '--tw-ring-color': 'var(--color-primary)' } as any} />
                        <button onClick={handleOtpVerify} disabled={loading || otpCode.length < 4} className="w-full py-4 rounded-xl text-white font-black shadow-md flex justify-center items-center gap-2 disabled:opacity-50 transition-all"
                            style={{ backgroundColor: 'var(--color-primary)' }}>
                            {loading ? <Loader2 className="animate-spin" /> : "Doğrula ve Devam Et"}
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}